import type { NormalizedOrders } from "../../modules/orders-reader/normalization";
import {
  splitIntoChunks,
  type OrdersParseTree,
} from "../../modules/orders-reader/parser";
import type { ItemNode } from "../../modules/orders-reader/parser/types";
import type { RateZone } from "./calibration";
import type { SpeechSegment, SpeechSegmentKind } from "./schedule";

interface StreamNode {
  readonly id: string;
  readonly kind: Exclude<SpeechSegmentKind, "chunk">;
}

function streamNodes(tree: OrdersParseTree): readonly StreamNode[] {
  const result: StreamNode[] = [];
  const addItem = (item: ItemNode): void => {
    result.push({ id: item.id, kind: "item" });
  };
  for (const paragraph of tree.paragraphs) {
    result.push({ id: paragraph.id, kind: "paragraph-heading" });
    paragraph.items.forEach(addItem);
    for (const subparagraph of paragraph.subparagraphs) {
      result.push({ id: subparagraph.id, kind: "subparagraph-heading" });
      subparagraph.items.forEach(addItem);
    }
  }
  return result;
}

function nodeZone(
  nodeId: string,
  criticalByNode: ReadonlyMap<string, readonly string[]>,
): RateZone {
  const kinds = criticalByNode.get(nodeId) ?? [];
  if (kinds.includes("mission")) return "mission";
  return kinds.length > 0 ? "critical" : "body";
}

export function ordersSpeechSegments(
  normalized: NormalizedOrders,
  maximumChunkLength = 200,
): readonly SpeechSegment[] {
  const nodes = streamNodes(normalized.displayTree);
  if (nodes.length !== normalized.spokenTextStream.length) {
    throw new Error(
      "The normalized speech stream does not match the parsed orders tree.",
    );
  }
  const criticalByNode = new Map<string, string[]>();
  for (const span of normalized.criticalSpans) {
    const kinds = criticalByNode.get(span.nodeId) ?? [];
    kinds.push(span.kind);
    criticalByNode.set(span.nodeId, kinds);
  }

  return nodes.flatMap((node, streamIndex) => {
    const text = normalized.spokenTextStream[streamIndex];
    /* v8 ignore next -- length equality above guarantees this invariant */
    if (text === undefined) return [];
    const chunks = splitIntoChunks(text, maximumChunkLength);
    const nodeIsHeading = node.kind !== "item";
    const zone = nodeIsHeading ? "heading" : nodeZone(node.id, criticalByNode);
    return chunks.map(
      (chunk, chunkIndex): SpeechSegment => ({
        id: `${node.id}/speech-${String(chunkIndex + 1).padStart(2, "0")}`,
        kind: chunkIndex === 0 ? node.kind : "chunk",
        text: chunk,
        zone,
      }),
    );
  });
}
